import axios, {
  AxiosRequestConfig,
  AxiosRequestTransformer,
  AxiosResponse,
} from "axios";
import {
  AUTH_REFRESH_TOKEN_URL,
  AUTH_SIGNIN_URL,
  AUTH_SIGNOUT_URL,
} from "./url_helper";
import { store } from "../store";
import {
  uploadAndCreateFlashCardProgress,
  uploadSkillVideoProgress,
} from "../store/actions";
import { uploadLectureVideoProgress } from "../store/lecture/actions";

// default
axios.defaults.baseURL = process.env.REACT_APP_API_URL;
// content type
axios.defaults.headers.post["Content-Type"] = "application/json";
axios.defaults.headers.common["Accept-Language"] = localStorage.getItem(
  "i18nextLng"
) as string;

let isRefreshing = false;
let failedQueue: any[] = [];

// token
const token = JSON.parse(localStorage.getItem("session") || "{}")
  ? JSON.parse(localStorage.getItem("session") || "{}")?.token
  : null;
if (token) axios.defaults.headers.common["Authorization"] = "Bearer " + token;

axios.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    const originalRequest = error.config;
    // logout a user, if any error occurred while refreshing token
    if (originalRequest.url.indexOf(AUTH_REFRESH_TOKEN_URL) > -1) {
      localStorage.removeItem("session");
      window.location.reload();
      return;
    }
    let message: any;
    switch (error?.response?.status) {
      case 500: {
        message = "Internal Server Error";
        break;
      }
      case 401: {
        // refresh token
        if (
          originalRequest.url != AUTH_SIGNIN_URL &&
          error?.response?.status === 401 &&
          !originalRequest._retry
        ) {
          if (isRefreshing) {
            // if already refreshing, push the failed request in queue
            return new Promise(function (resolve, reject) {
              failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers["Authorization"] = "Bearer " + token;
                return axios(originalRequest);
              })
              .catch((error) => {
                return Promise.reject(error);
              });
          }
          originalRequest._retry = true;
          isRefreshing = true;

          // refreshing token
          return new Promise(async function (resolve, reject) {
            var refreshToken = JSON.parse(
              localStorage.getItem("session") || "{}"
            )
              ? JSON.parse(localStorage.getItem("session") || "{}").refreshToken
              : null;
            if (!refreshToken) {
              localStorage.removeItem("session");
              window.location.reload();
              return;
            }
            axios
              .post(
                `${process.env.REACT_APP_API_URL}${AUTH_REFRESH_TOKEN_URL}`,
                {
                  refreshToken: refreshToken,
                  signingOut:
                    originalRequest.url.indexOf(AUTH_SIGNOUT_URL) > -1
                      ? true
                      : false,
                }
              )
              .then(async ({ data }) => {
                localStorage.setItem("session", JSON.stringify(data));
                axios.defaults.headers.common["Authorization"] =
                  "Bearer " + data.token;
                originalRequest.headers["Authorization"] =
                  "Bearer " + data.token;
                processQueue(null, data.token);
                resolve(axios(originalRequest));
              })
              .catch(async (error) => {
                processQueue(error, null);
                localStorage.removeItem("session");
                window.location.reload();
                reject(error);
              })
              .then(async () => {
                isRefreshing = false;
              });
          });
        } else {
          if (error?.response?.data?.message)
            message = error?.response?.data?.message;
          else message = error.message || "Unauthorized";
        }
        break;
      }
      case 400:
      case 409:
      case 406: {
        if (error?.response?.data?.errors)
          message = error?.response?.data?.errors;
        else if (error?.response?.data?.message)
          message = error?.response?.data?.message;
        else message = error.message || "Bad Request";
        break;
      }
      case 404: {
        if (error?.response?.data?.message)
          message = error?.response?.data?.message;
        else message = error.message || "Not found";
        break;
      }
      default:
        message = error.message || error;
    }
    return Promise.reject(message);
  }
);

const processQueue = (error: any, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (token: string) => {
  axios.defaults.headers.common["Authorization"] = "Bearer " + token;
};

class HttpClient {
  get = (url: string, params?: any) => {
    let response;

    let paramKeys: string[] = [];

    if (params) {
      Object.keys(params).map((key) => {
        paramKeys.push(key + "=" + params[key]);
        return paramKeys;
      });

      const queryString =
        paramKeys && paramKeys.length ? paramKeys.join("&") : "";
      response = axios.get(`${url}?${queryString}`, params);
    } else {
      response = axios.get(`${url}`, params);
    }

    return response;
  };
  /**
   * post given data to url
   *
   */
  post = (url: string, data: any) => {
    return axios.post(url, data, {
      transformRequest: [
        dateTimeTransformer,
        ...(axios.defaults.transformRequest as AxiosRequestTransformer[]),
      ],
    });
  };

  /**
   * upload file
   * @param url
   * @param data
   * @returns
   */
  uploadFile = (url: string, data: any) => {
    var bodyFormData = new FormData();
    bodyFormData.append("file", data);
    return axios.post(url, bodyFormData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

  /**
   * upload image with progress
   * @param url
   * @param data
   * @param callback
   * @param localId
   * @returns
   */
  uploadFileWithProgress = (
    url: string,
    data: any,
    callback: (progress: number, localId: number) => void,
    localId: number
  ) => {
    var bodyFormData = new FormData();
    bodyFormData.append("file", data);
    return axios.post(url, bodyFormData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (event: ProgressEvent) => {
        const progress = (event.loaded / event.total) * 100;
        callback(progress, localId);
      },
    });
  };

  /**
   * upload file with progress in flash card reducer
   * @param url
   * @param data
   * @returns
   */
  uploadFlashCardFile = (url: string, data: any, localId?: number) => {
    var bodyFormData = new FormData();
    bodyFormData.append("file", data);
    return axios.post(url, bodyFormData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (event: ProgressEvent) => {
        const progress = (event.loaded / event.total) * 100;
        store.dispatch(
          uploadAndCreateFlashCardProgress({
            progress: Math.round(progress),
            localId: localId ? localId : 1,
          })
        );
      },
    });
  };

  downloadFile = (url: string, params?: any) => {
    let response;

    let paramKeys: string[] = [];

    if (params) {
      Object.keys(params).map((key) => {
        paramKeys.push(key + "=" + params[key]);
        return paramKeys;
      });

      const queryString =
        paramKeys && paramKeys.length ? paramKeys.join("&") : "";
      response = axios.get(`${url}?${queryString}`, params);
    } else {
      response = axios.get(`${url}`, { ...params, responseType: "blob" });
    }

    return response;
  };

  uploadSkillVideoFile = (url: string, data: any) => {
    var bodyFormData = new FormData();
    bodyFormData.append("file", data);
    return axios.post(url, bodyFormData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (event: ProgressEvent) => {
        const progress = (event.loaded / event.total) * 100;
        store.dispatch(uploadSkillVideoProgress(Math.round(progress)));
      },
    });
  };

  uploadLectureVideoFile = (url: string, data: any) => {
    var bodyFormData = new FormData();
    bodyFormData.append("file", data);
    return axios.post(url, bodyFormData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (event: ProgressEvent) => {
        const progress = (event.loaded / event.total) * 100;
        store.dispatch(uploadLectureVideoProgress(Math.round(progress)));
      },
    });
  };

  /**
   * Updates data
   */
  update = (url: string, data: any) => {
    return axios.patch(url, data);
  };

  put = (url: string, data: any) => {
    return axios.put(url, data, {
      transformRequest: [
        dateTransformer,
        ...(axios.defaults.transformRequest as AxiosRequestTransformer[]),
      ],
    });
  };

  patch = (url: string, data: any) => {
    return axios.patch(url, data, {
      transformRequest: [
        dateTransformer,
        ...(axios.defaults.transformRequest as AxiosRequestTransformer[]),
      ],
    });
  };

  /**
   * Delete
   */
  delete = (url: string, config?: AxiosRequestConfig) => {
    return axios.delete(url, { ...config });
  };
}

const getLoggedInUserSession = () => {
  const user = localStorage.getItem("session");
  if (!user) {
    return null;
  } else {
    return JSON.parse(user);
  }
};

const dateTransformer: AxiosRequestTransformer = (data) => {
  if (data instanceof Date) {
    // do your specific formatting here
    var day = data.getDate();
    var year = data.getFullYear();
    var month = data.getMonth() + 1;
    return `${month}-${day}-${year}`;
  }
  if (Array.isArray(data)) {
    return data.map((val) => dateTransformer(val));
  }
  if (typeof data === "object" && data !== null) {
    return Object.fromEntries(
      Object.entries(data).map(([key, val]) => [key, dateTransformer(val)])
    );
  }
  return data;
};

const dateTimeTransformer: AxiosRequestTransformer = (data) => {
  if (data instanceof Date) {
    // Format each part with leading zeros
    const seconds = String(data.getSeconds()).padStart(2, "0");
    const minutes = String(data.getMinutes()).padStart(2, "0");
    const hours = String(data.getHours()).padStart(2, "0");
    const day = String(data.getDate()).padStart(2, "0");
    const year = data.getFullYear();
    const month = String(data.getMonth() + 1).padStart(2, "0");
    return `${month}-${day}-${year} ${hours}:${minutes}:${seconds}`;
  }
  if (Array.isArray(data)) {
    return data.map((val) => dateTimeTransformer(val));
  }
  if (typeof data === "object" && data !== null) {
    return Object.fromEntries(
      Object.entries(data).map(([key, val]) => [key, dateTimeTransformer(val)])
    );
  }
  return data;
};

export { HttpClient, setAuthorization, getLoggedInUserSession };
